import axios from 'axios';
import { format } from 'date-fns';
import { 
  toLocalDateString, 
  createLocalDate, 
  formatLocalDate, 
  today
} from '../utils/dateUtils';

// Add a caching system
const cache = {
  data: {},
  timestamps: {},
  
  // Check if caching is enabled
  isEnabled: function() {
    return localStorage.getItem('cacheEnabled') === 'true';
  },
  
  // Get the cache timeout from localStorage or use default (5 minutes)
  getMaxAge: function() {
    const storedTimeout = localStorage.getItem('cacheTimeout');
    return storedTimeout ? parseInt(storedTimeout) * 1000 : 5 * 60 * 1000;
  },

  // Get cached data if it exists and is not expired
  get: function(key) {
    // Return null if caching is disabled
    if (!this.isEnabled()) return null;
    
    if (!this.data[key]) return null;
    
    const timestamp = this.timestamps[key] || 0;
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - timestamp > this.getMaxAge()) {
      // Cache expired, remove it
      delete this.data[key];
      delete this.timestamps[key];
      return null;
    }
    
    return this.data[key];
  },
  
  // Set cache data with current timestamp
  set: function(key, data) {
    // Don't cache if caching is disabled
    if (!this.isEnabled()) return;
    
    this.data[key] = data;
    this.timestamps[key] = Date.now();
  },
  
  // Clear specific cache entry
  clear: function(key) {
    delete this.data[key];
    delete this.timestamps[key];
  },
  
  // Clear all cache
  clearAll: function() {
    this.data = {};
    this.timestamps = {};
  }
};

// Determine base URL dynamically based on environment and current host
let API_BASE_URL = '/api';

// In development, use relative URL to avoid CORS issues
if (process.env.NODE_ENV === 'development') {
  // Use relative URL which will work with proxy configuration
  API_BASE_URL = '/api';
  console.log('Using relative API base URL:', API_BASE_URL);
}

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000, // 10 second timeout
  validateStatus: function (status) {
    return status >= 200 && status < 400; // Don't reject if status is between 200-399
  }
});

// Create a separate axios instance for QA operations with longer timeout
const qaApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 300000, // 5 minute timeout for QA operations
  validateStatus: function (status) {
    return status >= 200 && status < 400;
  }
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add the same request interceptor to QA API client
qaApiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('QA Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add error interceptor to handle common error responses
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);

    // Handle network errors
    if (!error.response) {
      console.error('Network error - no response received');
      if (error.code === 'ECONNABORTED') {
        error.userMessage = 'Request timed out. Please try again.';
      } else {
        error.userMessage = 'Network error. Please check your connection and try again.';
      }
      return Promise.reject(error);
    }

    // Format error message based on response
    console.error('Request URL:', error.config.url);
    console.error('Request method:', error.config.method);
    console.error('Request data:', error.config.data);
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
    console.error('Response headers:', error.response.headers);

    // Handle authentication errors
    if (error.response.status === 401 ||
        (error.response.data && error.response.data.msg === "Signature verification failed")) {

      // If backend requests step-up 2FA, do not auto-logout; let caller handle it
      if (error.response.data && error.response.data.requires_2fa) {
        error.userMessage = error.response.data.message || 'Two-factor authentication required';
        return Promise.reject(error);
      }
      
      // Don't auto-logout for password change endpoint - let the component handle it
      const isPasswordChangeRequest = error.config.url && (
        error.config.url.includes('/auth/change-password') || 
        error.config.url.includes('change-password')
      );
      const isCheckAuthRequest = error.config.url && error.config.url.includes('/auth/check-auth');
      
      console.log('Authentication error detected:');
      console.log('- URL:', error.config.url);
      console.log('- Full config:', error.config);
      console.log('- Is password change request:', isPasswordChangeRequest);
      console.log('- Is check auth request:', isCheckAuthRequest);
      
      if (isPasswordChangeRequest) {
        console.log('Password change request failed - not auto-logging out');
        error.userMessage = error.response.data?.error || 'Password change failed';
      } else if (isCheckAuthRequest) {
        console.log('Check auth request failed - not auto-logging out');
        error.userMessage = 'Authentication check failed';
      } else {
        console.error('Authentication error - clearing token and redirecting to landing page');
        localStorage.removeItem('token');
        // Redirect to landing page instead of login page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        error.userMessage = 'Your session has expired. Please log in again.';
      }
    }

    // Add specific handling for 422 errors
    if (error.response.status === 422) {
      // Log the request that caused the error
      console.log('Request that failed:', error.config.data);
      error.userMessage = error.response.data.error || 'Validation failed - please check your input';
    }

    // Handle 404 errors
    if (error.response.status === 404) {
      error.userMessage = 'The requested resource was not found.';
    }

    // Handle 500 errors
    if (error.response.status >= 500) {
      error.userMessage = 'Server error. Please try again later.';
    }

    return Promise.reject(error);
  }
);

// Add response interceptor to QA API client with longer timeout handling
qaApiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('QA API Error:', error);

    // Handle network errors
    if (!error.response) {
      console.error('QA Network error - no response received');
      if (error.code === 'ECONNABORTED') {
        error.userMessage = 'QA operation timed out. This may take several minutes for large test suites.';
      } else {
        error.userMessage = 'Network error during QA operation. Please check your connection and try again.';
      }
      return Promise.reject(error);
    }

    // Handle authentication errors
    if (error.response.status === 401 ||
        (error.response.data && error.response.data.msg === "Signature verification failed")) {
      error.userMessage = 'Authentication required for QA operations. Please log in again.';
      return Promise.reject(error);
    }

    // Handle 404 errors
    if (error.response.status === 404) {
      error.userMessage = 'QA endpoint not found. Please check if the QA service is running.';
    }

    // Handle 500 errors
    if (error.response.status >= 500) {
      error.userMessage = 'QA server error. Please try again later.';
    }

    return Promise.reject(error);
  }
);

// Helper function to generate cache keys
const generateCacheKey = (endpoint, params = null) => {
  if (params) {
    return `${endpoint}_${JSON.stringify(params)}`;
  }
  return endpoint;
};

// Export the cache so it can be accessed from App.js
export { cache };

// API modules
export const authApi = {
  register: (userData) => apiClient.post('/auth/register', userData),
  login: (credentials) => {
    console.log('authApi.login called with:', { ...credentials, password: '***' });
    return apiClient.post('/auth/login', credentials).then(response => {
      console.log('authApi.login response:', response);
      return response;
    }).catch(error => {
      console.error('authApi.login error:', error);
      throw error;
    });
  },
  checkAuth: () => apiClient.get('/auth/check-auth'),
  getProfile: () => apiClient.get('/auth/profile'),
  // 2FA endpoints
  setup2FA: () => apiClient.post('/auth/setup-2fa'),
  verify2FASetup: (code) => apiClient.post('/auth/verify-2fa-setup', { code }),
  disable2FA: (password, twoFACode) => apiClient.post('/auth/disable-2fa', { 
    password, 
    two_fa_code: twoFACode 
  }),
  verify2FA: (code) => apiClient.post('/auth/verify-2fa', { code }),
  // Trusted device endpoints
  trustDevice: (code, deviceName) => apiClient.post('/auth/trust-device', { 
    code, 
    device_name: deviceName 
  }),
  getTrustedDevices: () => apiClient.get('/auth/trusted-devices'),
  // device_id (a stable, unique fingerprint the backend returns per device
  // - see User.device_fingerprint()) identifies which trusted device to
  // remove; device_name isn't unique (two devices can share the same OS/
  // browser combo) so it's kept only as a fallback for callers that don't
  // have a device_id yet.
  removeTrustedDevice: (code, deviceId, deviceName) => apiClient.post('/auth/remove-trusted-device', {
    code,
    device_id: deviceId,
    device_name: deviceName
  }),
  // Email verification endpoints
  verifyEmail: (token, email) => apiClient.post('/auth/verify-email', { token, email }),
  resendVerification: (email) => apiClient.post('/auth/resend-verification', { email }),
  getEmailVerificationStatus: () => apiClient.get('/auth/verify-email-status')
};

// Sessions API
export const sessionsApi = {
  list: () => apiClient.get('/auth/sessions'),
  revoke: (sessionId, twoFACode = null) => {
    const data = twoFACode ? { two_fa_code: twoFACode } : undefined;
    return apiClient.delete(`/auth/sessions/${sessionId}`, { data });
  },
  revokeAll: (excludeCurrent = false, twoFACode = null) => {
    const body = { exclude_current: excludeCurrent };
    if (twoFACode) body.two_fa_code = twoFACode;
    return apiClient.post('/auth/sessions/revoke_all', body);
  }
};

export const transactionsApi = {
  getAll: async (params) => {
    // Format date parameters if they are Date objects
    const formattedParams = { ...params };
    if (formattedParams.date_from instanceof Date) {
      formattedParams.date_from = toLocalDateString(formattedParams.date_from); // YYYY-MM-DD format
    }
    if (formattedParams.date_to instanceof Date) {
      formattedParams.date_to = toLocalDateString(formattedParams.date_to); // YYYY-MM-DD format
    }
    
    // Ensure tag_id is properly formatted as a number if present
    if (formattedParams.tag_id) {
      // Make sure it's treated as number if it was passed as string
      formattedParams.tag_id = parseInt(formattedParams.tag_id);
      if (isNaN(formattedParams.tag_id)) {
        delete formattedParams.tag_id; // Remove invalid tag_id
      }
    }
    
    // If transaction type is provided, make sure it's properly formatted for the backend
    if (formattedParams.type) {
      // The backend expects uppercase for enum lookup
      const originalType = formattedParams.type;
      console.log('Original transaction type:', originalType);
      
      // Fix for transaction type issues - direct mapping to ensure correct values
      // This protects against any potential URL encoding or backend issues
      if (originalType === 'Refund') {
        formattedParams.type = 'REFUND';
      } else if (originalType === 'Income') {
        formattedParams.type = 'INCOME';
      } else if (originalType === 'Expense') {
        formattedParams.type = 'EXPENSE';
      } else if (originalType === 'Transfer') {
        formattedParams.type = 'TRANSFER';
      } else {
        // Fallback to uppercase conversion for any other values
        formattedParams.type = formattedParams.type.toUpperCase();
      }
      console.log('Formatted transaction type:', formattedParams.type);
    }
    
    // Remove empty string values that shouldn't be sent to the backend
    const cleanedParams = { ...formattedParams };
    Object.keys(cleanedParams).forEach(key => {
      if (cleanedParams[key] === '') {
        delete cleanedParams[key];
      }
    });
    
    // Add detailed logging
    console.log('Fetching transactions with cleaned params:', cleanedParams);
    console.table(cleanedParams); // Table format for better visualization
    
    // Check cache first
    const cacheKey = generateCacheKey('/transactions', cleanedParams);
    const cachedData = cache.get(cacheKey);
    
    if (cachedData) {
      console.log('Using cached transaction data');
      return cachedData;
    }
    
    const response = await apiClient.get('/transactions', { params: cleanedParams });
    
    // Add client-side validation for category filter as a safety measure
    // This ensures category filtering is strictly enforced even if the backend has issues
    if (cleanedParams.category && response.data && Array.isArray(response.data)) {
      console.log(`API validation: Enforcing exact category match for "${cleanedParams.category}"`);
      const originalCount = response.data.length;
      
      // Filter to only include exact category matches
      response.data = response.data.filter(transaction => 
        transaction.category === cleanedParams.category
      );
      
      console.log(`API validation: Filtered ${originalCount} transactions to ${response.data.length} with exact category match`);
    }
    
    // Cache the response
    cache.set(cacheKey, response);
    
    return response;
  },
  get: (id) => apiClient.get(`/transactions/${id}`),
  add: (transaction) => {
    // Clear transactions cache when adding new transaction
    Object.keys(cache.data).forEach(key => {
      if (key.startsWith('/transactions')) {
        cache.clear(key);
      }
      // Also clear account transaction caches since the new transaction may affect account views
      if (key.startsWith('/accounts/') && key.includes('/transactions')) {
        cache.clear(key);
      }
    });
    return apiClient.post('/transactions', transaction);
  },
  update: (id, transaction) => {
    // Clear transactions cache when updating a transaction
    Object.keys(cache.data).forEach(key => {
      if (key.startsWith('/transactions')) {
        cache.clear(key);
      }
      // Also clear account transaction caches since the updated transaction may affect account views
      if (key.startsWith('/accounts/') && key.includes('/transactions')) {
        cache.clear(key);
      }
    });
    return apiClient.put(`/transactions/${id}`, transaction);
  },
  delete: (id) => {
    // Clear transactions cache when deleting a transaction
    Object.keys(cache.data).forEach(key => {
      if (key.startsWith('/transactions')) {
        cache.clear(key);
      }
      // Also clear account transaction caches since the deleted transaction may affect account views
      if (key.startsWith('/accounts/') && key.includes('/transactions')) {
        cache.clear(key);
      }
    });
    return apiClient.delete(`/transactions/${id}`);
  }
};

// Transaction Details API
export const transactionDetailsApi = {
  getAll: (transactionId) => apiClient.get(`/transactions/${transactionId}/details`),
  create: (transactionId, detail) => apiClient.post(`/transactions/${transactionId}/details`, detail),
  update: (detailId, detail) => apiClient.put(`/transactions/details/${detailId}`, detail),
  delete: (detailId) => apiClient.delete(`/transactions/details/${detailId}`),
  bulkCreate: (transactionId, details) => apiClient.post(`/transactions/${transactionId}/details/bulk`, { details })
};

export const categoriesApi = {
  getAll: async () => {
    // Check cache first
    const cacheKey = '/categories';
    const cachedData = cache.get(cacheKey);
    
    if (cachedData) {
      console.log('Using cached categories data');
      return cachedData;
    }
    
    const response = await apiClient.get('/categories');
    
    // Cache the response
    cache.set(cacheKey, response);
    
    return response;
  },
  get: (id) => apiClient.get(`/categories/${id}`),
  add: (category) => {
    // Clear categories cache when adding new category
    cache.clear('/categories');
    return apiClient.post('/categories', category);
  },
  update: (id, category) => {
    // Clear categories cache when updating a category
    cache.clear('/categories');
    return apiClient.put(`/categories/${id}`, category);
  },
  delete: (id, data = {}) => {
    // Clear categories cache when deleting a category
    cache.clear('/categories');
    return apiClient.delete(`/categories/${id}`, { data });
  },
  getDeleted: () => apiClient.get('/categories/deleted'),
  restore: (id) => {
    // Clear categories cache when restoring a category
    cache.clear('/categories');
    return apiClient.post(`/categories/${id}/restore`);
  },
  hardDelete: (id) => {
    // Clear categories cache when hard-deleting a category
    cache.clear('/categories');
    return apiClient.delete(`/categories/${id}/hard-delete`);
  },
  getTransactions: (id, year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    return apiClient.get(`/categories/${id}/transactions?${params.toString()}`);
  },
  getMonthlySpending: () => apiClient.get('/summary/monthly'),
  hasTransactions: (id) => apiClient.get(`/categories/${id}/transactions?count_only=true`),
};

export const accountsApi = {
  getAll: async () => {
    // Check cache first
    const cacheKey = '/accounts';
    const cachedData = cache.get(cacheKey);
    
    if (cachedData) {
      console.log('Using cached accounts data');
      return cachedData;
    }
    
    const response = await apiClient.get('/accounts');
    
    // Cache the response
    cache.set(cacheKey, response);
    
    return response;
  },
  get: (id) => apiClient.get(`/accounts/${id}`),
  getStructure: () => apiClient.get('/accounts/structure'),
  add: (account) => {
    // Clear accounts cache when adding new account
    cache.clear('/accounts');
    return apiClient.post('/accounts', account);
  },
  update: (id, account) => {
    // Clear accounts cache when updating an account
    cache.clear('/accounts');
    console.log(`Updating account with ID: ${id}`, account);
    // Ensure id is a number for API endpoint
    const accountId = parseInt(id);
    if (isNaN(accountId)) {
      console.error(`Invalid account ID for update: ${id}`);
      return Promise.reject(new Error(`Invalid account ID: ${id}. API expects numeric IDs.`));
    }
    return apiClient.put(`/accounts/${accountId}`, account);
  },
  delete: (id, data = {}) => {
    // Clear accounts cache when deleting an account
    cache.clear('/accounts');
    return apiClient.delete(`/accounts/${id}`, { data });
  },
  getTransactions: async (id, year, month, filters = {}) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);

    // Add date filter parameters
    if (filters.date_from) {
      // Format date to YYYY-MM-DD if it's a Date object
      const dateFrom = filters.date_from instanceof Date 
        ? toLocalDateString(filters.date_from)
        : filters.date_from;
      params.append('date_from', dateFrom);
    }
    if (filters.date_to) {
      // Format date to YYYY-MM-DD if it's a Date object
      const dateTo = filters.date_to instanceof Date 
        ? toLocalDateString(filters.date_to)
        : filters.date_to;
      params.append('date_to', dateTo);
    }

    // Add filter parameters
    if (filters.type) {
      // Format transaction type for backend
      if (filters.type === 'Refund') {
        params.append('type', 'REFUND');
      } else if (filters.type === 'Income') {
        params.append('type', 'INCOME');
      } else if (filters.type === 'Expense') {
        params.append('type', 'EXPENSE');
      } else if (filters.type === 'Transfer') {
        params.append('type', 'TRANSFER');
      } else {
        params.append('type', filters.type.toUpperCase());
      }
    }
    if (filters.category) params.append('category', filters.category);
    if (filters.amount) params.append('amount', filters.amount);
    if (filters.keyword) params.append('keyword', filters.keyword);
    if (filters.tag_id) params.append('tag_id', filters.tag_id);

    // Generate cache key
    const cacheKey = generateCacheKey(`/accounts/${id}/transactions`, {
      year, month, ...filters
    });
    
    // Check cache first
    const cachedData = cache.get(cacheKey);
    if (cachedData) {
      console.log('Using cached account transactions data');
      return cachedData;
    }
    
    const response = await apiClient.get(`/accounts/${id}/transactions${params.toString() ? '?' + params.toString() : ''}`);
    
    // Cache the response
    cache.set(cacheKey, response);
    
    return response;
  },
  getDeleted: () => apiClient.get('/accounts/deleted'),
  restore: (id) => {
    // Clear accounts cache when restoring an account
    cache.clear('/accounts');
    return apiClient.post(`/accounts/${id}/restore`);
  },
  hardDelete: (id) => {
    // Clear accounts cache when hard-deleting an account
    cache.clear('/accounts');
    return apiClient.delete(`/accounts/${id}/hard-delete`);
  },
  debugRecurring: (id) => apiClient.get(`/accounts/${id}/debug-recurring`)
};

export const budgetsApi = {
  getAll: (categoryId) => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId);
    return apiClient.get('/budgets', { params });
  },
  get: (id) => apiClient.get(`/budgets/${id}`),
  add: (budget) => apiClient.post('/budgets', budget),
  update: (id, budget) => apiClient.put(`/budgets/${id}`, budget),
  delete: (id) => apiClient.delete(`/budgets/${id}`)
};

export const summaryApi = {
  getMonthly: (year, month) => {
    console.log(`summaryApi.getMonthly called with year=${year}, month=${month}`);
    // Make sure parameters are included in the request
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    
    console.log("API request params:", params);
    return apiClient.get('/summary/monthly', { params });
  },
  getMonthlyAnalysis: (year, month) => {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    
    return apiClient.get('/summary/monthly-analysis', { params });
  },
  generateMonthlyReport: (year, month, reportType = 'email') => {
    return apiClient.post('/summary/monthly-report', {
      year,
      month,
      type: reportType
    });
  }
};

// Tags API methods
export const tagsApi = {
  getAll: () => apiClient.get('/tags'),
  getSummary: () => apiClient.get('/tags/summary'),
  create: (tag) => apiClient.post('/tags', tag),
  update: (id, tag) => apiClient.put(`/tags/${id}`, tag),
  delete: (id) => apiClient.delete(`/tags/${id}`)
};

// Helper to clear all merchant-related cache entries
const clearMerchantsCache = () => {
  Object.keys(cache.data).forEach(key => {
    if (key.startsWith('/merchants')) {
      cache.clear(key);
    }
  });
};

// Merchants API methods
export const merchantsApi = {
  getAll: async (params = {}) => {
    const cacheKey = generateCacheKey('/merchants', params);
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const shouldFetchAllPages =
      !params.page &&
      !params.per_page &&
      !params.search &&
      !params.type;

    if (!shouldFetchAllPages) {
      const response = await apiClient.get('/merchants', { params });
      cache.set(cacheKey, response);
      return response;
    }

    const perPage = 100;
    const extractMerchants = (response) =>
      Array.isArray(response.data?.merchants)
        ? response.data.merchants
        : Array.isArray(response.data)
          ? response.data
          : [];

    // Fetch page 1 first to learn how many pages there are in total, then
    // fetch the rest in parallel instead of awaiting each page one at a time
    // - turns N sequential round trips into a single round trip (bounded by
    // the slowest request) once the page count is known.
    const firstResponse = await apiClient.get('/merchants', {
      params: { ...params, page: 1, per_page: perPage }
    });

    const totalPages = Number(firstResponse.data?.pages) || 1;
    let aggregatedMerchants = extractMerchants(firstResponse);
    let lastResponse = firstResponse;

    if (totalPages > 1) {
      const remainingPageNumbers = Array.from(
        { length: totalPages - 1 },
        (_, index) => index + 2
      );

      const remainingResponses = await Promise.all(
        remainingPageNumbers.map((page) =>
          apiClient.get('/merchants', { params: { ...params, page, per_page: perPage } })
        )
      );

      for (const response of remainingResponses) {
        aggregatedMerchants = aggregatedMerchants.concat(extractMerchants(response));
      }
      lastResponse = remainingResponses[remainingResponses.length - 1] || firstResponse;
    }

    const aggregatedResponse = {
      ...lastResponse,
      data: {
        ...(lastResponse?.data || {}),
        merchants: aggregatedMerchants,
        total: aggregatedMerchants.length,
        page: 1,
        pages: 1,
        has_next: false,
        has_prev: false
      }
    };

    cache.set(cacheKey, aggregatedResponse);
    return aggregatedResponse;
  },
  get: (id) => apiClient.get(`/merchants/${id}`),
  create: (merchant) => {
    clearMerchantsCache(); // Clear cache when creating
    return apiClient.post('/merchants', merchant);
  },
  update: (id, merchant) => {
    clearMerchantsCache(); // Clear cache when updating
    return apiClient.put(`/merchants/${id}`, merchant);
  },
  delete: (id) => {
    clearMerchantsCache(); // Clear cache when deleting
    return apiClient.delete(`/merchants/${id}`);
  },
  search: (query) => {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    return apiClient.get(`/merchants/search?${params.toString()}`);
  },
  getTypes: () => {
    const cacheKey = '/merchants/types';
    const cached = cache.get(cacheKey);
    if (cached) return Promise.resolve(cached);

    return apiClient.get('/merchants/types').then(response => {
      cache.set(cacheKey, response);
      return response;
    });
  },
  getSummary: (params = {}) => apiClient.get('/merchants/summary', { params })
};

// Currencies API methods
export const currenciesApi = {
  getAll: () => apiClient.get('/currencies'),
  getAllIncludingInactive: () => apiClient.get('/currencies/all'),
  get: (code) => apiClient.get(`/currencies/${code}`),
  create: (currency) => apiClient.post('/currencies', currency),
  update: (code, currency) => apiClient.put(`/currencies/${code}`, currency),
  getExchangeRates: (params) => apiClient.get('/currencies/exchange-rates', { params }),
  createExchangeRate: (exchangeRate) => apiClient.post('/currencies/exchange-rates', exchangeRate),
  convertAmount: (amount, fromCurrency, toCurrency, date) => {
    const params = new URLSearchParams();
    params.append('amount', amount);
    params.append('from', fromCurrency);
    params.append('to', toCurrency);
    if (date) params.append('date', date);
    return apiClient.get(`/currencies/convert?${params.toString()}`);
  },
  updateRates: (baseCurrency) => apiClient.post('/currencies/update-rates', { base_currency: baseCurrency })
};

// Add this debugging function to your API client
export const debugToken = () => {
  const token = localStorage.getItem('token');

  if (!token) {
    console.log('No token found in localStorage');
    return;
  }

  // Basic JWT structure check
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.log('Token does not appear to be a valid JWT (should have 3 parts)');
    return;
  }

  try {
    // Decode the payload (middle part)
    const payload = JSON.parse(atob(parts[1]));
    console.log('Token payload:', payload);
    console.log('Token expiration:', new Date(payload.exp * 1000));
    console.log('Current time:', new Date());
    console.log('Token expired?', payload.exp * 1000 < Date.now());
  } catch (e) {
    console.log('Error decoding token:', e);
  }
};

// Add this debugging utility function
export const debugAuthToken = () => {
  const token = localStorage.getItem('token');

  if (!token) {
    console.error('No token found in localStorage');
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Token is not a valid JWT (should have 3 parts)');
      return null;
    }

    // Parse the payload
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));

    // Check critical fields
    const issues = [];

    if (!payload.sub) issues.push('Missing subject claim');
    else if (typeof payload.sub !== 'string') issues.push(`Subject is not a string: ${typeof payload.sub}`);

    if (!payload.exp) issues.push('Missing expiration claim');
    else if (payload.exp < Date.now()/1000) issues.push('Token expired');

    const result = {
      valid: issues.length === 0,
      issues,
      payload,
      expiresAt: payload.exp ? new Date(payload.exp * 1000) : 'unknown'
    };

    console.log('Token analysis:', result);
    return result;
  } catch (e) {
    console.error('Error analyzing token:', e);
    return null;
  }
};

// Add debugging helper for trusted device tokens
export const debugTrustedDevice = () => {
  const token = localStorage.getItem('trustedDeviceToken');
  
  console.log('🔍 [DEBUG] Trusted Device Token Debug:', {
    exists: !!token,
    length: token ? token.length : 0,
    preview: token ? token.substring(0, 10) + '...' : 'none',
    fullToken: token,
    localStorage: {
      token: localStorage.getItem('token') ? 'exists' : 'missing',
      trustedDeviceToken: localStorage.getItem('trustedDeviceToken') ? 'exists' : 'missing',
      keepSignedIn: localStorage.getItem('keepSignedIn'),
      rememberedIdentifier: localStorage.getItem('rememberedIdentifier')
    }
  });
  
  return token;
};

// Make debugging function available globally for easy console access
window.debugTrustedDevice = debugTrustedDevice;

// Weather API methods
export const weatherApi = {
  getCurrent: (city, units = 'imperial', includeForecast = false) =>
    apiClient.get(`/weather/current?city=${encodeURIComponent(city)}&units=${units}&forecast=${includeForecast}`),
  getForecast: (city, units = 'imperial') =>
    apiClient.get(`/weather/forecast?city=${encodeURIComponent(city)}&units=${units}`),
  getWeather: () => apiClient.get('/weather')
};

// Recurring Transactions API
export const recurringTransactionsApi = {
  getAll: () => apiClient.get('/recurring-transactions'),
  getById: (id) => apiClient.get(`/recurring-transactions/${id}`),
  create: (data) => apiClient.post('/recurring-transactions', data),
  update: (id, data) => apiClient.put(`/recurring-transactions/${id}`, data),
  delete: (id) => apiClient.delete(`/recurring-transactions/${id}`),
  activate: (id) => apiClient.post(`/recurring-transactions/${id}/activate`),
  deactivate: (id) => apiClient.post(`/recurring-transactions/${id}/deactivate`),
  process: () => apiClient.post('/recurring-transactions/process'),
  processSingle: (id, options = {}) => apiClient.post(`/recurring-transactions/${id}/process`, options),
  skipSingle: (id) => apiClient.post(`/recurring-transactions/${id}/skip`),
  debugStatus: () => apiClient.get('/recurring-transactions/debug/status'),
  getProcessingSettings: () => apiClient.get('/recurring-transactions/processing-mode'),
  updateProcessingSettings: (data) => apiClient.put('/recurring-transactions/processing-mode', data)
};

// Goals API methods
export const goalsApi = {
  getAll: (params = {}) => apiClient.get('/goals', { params }),
  getById: (id) => apiClient.get(`/goals/${id}`),
  create: (goalData) => apiClient.post('/goals', goalData),
  update: (id, goalData) => apiClient.put(`/goals/${id}`, goalData),
  delete: (id) => apiClient.delete(`/goals/${id}`),
  getTransactions: (id) => apiClient.get(`/goals/${id}/transactions`),
  tagTransaction: (id, transactionData) => apiClient.post(`/goals/${id}/transactions`, transactionData),
  untagTransaction: (id, transactionId) => apiClient.delete(`/goals/${id}/transactions/${transactionId}`),
  getProgress: (id) => apiClient.get(`/goals/${id}/progress`),
  calculateContributions: (data) => apiClient.post('/goals/calculate-contributions', data)
};

export const feedbackApi = {
  list: (params = {}) =>
    apiClient.get('/feedback', {
      params: {
        per_page: 50,
        sort_by: 'score',
        sort_order: 'desc',
        ...params
      }
    }),
  create: (payload) => apiClient.post('/feedback', payload),
  vote: (feedbackId, direction) =>
    apiClient.post(`/feedback/${feedbackId}/vote`, { direction }),
  updateStatus: (feedbackId, status) =>
    apiClient.patch(`/feedback/${feedbackId}/status`, { status }),
  archive: (feedbackId) => apiClient.delete(`/feedback/${feedbackId}`)
};

// QA API methods - using qaApiClient with longer timeout
export const qaApi = {
  // Get available tests
  getAvailableTests: () => qaApiClient.get('/qa/tests/available'),
  
  // Get system info
  getSystemInfo: () => qaApiClient.get('/qa/system-info'),
  
  // Get dashboard summary
  getDashboardSummary: () => qaApiClient.get('/qa/dashboard/summary'),
  
  // Test run management
  getTestRuns: (params = {}) => qaApiClient.get('/qa/test-runs', { params }),
  createTestRun: (config) => qaApiClient.post('/qa/test-runs', config),
  getTestRun: (id) => qaApiClient.get(`/qa/test-runs/${id}`),
  startTestRun: (id) => qaApiClient.post(`/qa/test-runs/${id}/start`),
  stopTestRun: (id) => qaApiClient.post(`/qa/test-runs/${id}/stop`),
  deleteTestRun: (id) => qaApiClient.delete(`/qa/test-runs/${id}`),
  
  // Test execution and progress
  getTestExecutions: (testRunId) => qaApiClient.get(`/qa/test-runs/${testRunId}/executions`),
  getTestProgress: (testRunId) => qaApiClient.get(`/qa/test-runs/${testRunId}/progress`),
  
  // Debug endpoints
  debugTestRunner: () => qaApiClient.get('/qa/tests/debug'),
  
  // User stats
  getUserStats: () => qaApiClient.get('/qa/user-stats'),
  // Users listing with optional server-side search params
  getUsers: (params = {}) => qaApiClient.get('/qa/users', { params }),
  // User details and admin updates
  getUserById: (userId) => qaApiClient.get(`/qa/users/${userId}`),
  updateUserSubscriptionStatus: (userId, subscriptionStatus) =>
    qaApiClient.put(`/qa/users/${userId}/subscription-status`, { subscription_status: subscriptionStatus }),
};

// Admin API methods
export const adminApi = {
  getEmailProvider: () => apiClient.get('/settings/email-provider'),
  updateEmailProvider: (provider) =>
    apiClient.put('/settings/email-provider', { provider }),
};

export const systemApi = {
  getCommitHistory: (limit = 10) =>
    apiClient.get('/system/commits', { params: { limit } })
};

export default apiClient;
